// Vendor Libs
import {difference, find, filter, get, isEmpty, isEqual, isFunction, map, max, pick, reduce, union, xor} from 'lodash';

// Data
import actionsServiceCaller from '../lib/app/actions-service-caller';

// Configs
import servicesFormConfig from '../../config/services-form-config';

const flaggedMetadataMiddleware = (store) => (next) => (action) => {
    const {dispatch, getState} = store;
    const state = getState();

    next(action);

    const nextState = getState();

    const actions = {
        FLAGGED_METADATA_ADD_ISSUE: () => handleAddIssue(dispatch, nextState),
        FLAGGED_METADATA_DELETE_ISSUE: () => handleDeleteIssue(dispatch, action, nextState),
        FLAGGED_METADATA_RESET: () => handleReset(dispatch),
        FLAGGED_METADATA_SAVE_COMMENTS: () => saveComments(dispatch, action, nextState),
        FLAGGED_METADATA_SET_ACTIVE: () => handleSetActive(dispatch, action, nextState),
        FLAGGED_METADATA_SUBMIT_POST: () => postFlaggedMetadata(dispatch, action, nextState),
        FLAGGED_METADATA_POST_FULFILLED: () => handleCompleteFlagging(dispatch, action, nextState),
        FORM_SET_FIELD_VALUE: () => handleFormFieldUpdate(dispatch, action, state, nextState),
        FORM_SUBMIT_REJECTED: () => handleFormSubmitRejected(dispatch, action, nextState),
        SEARCH_DATA_FETCH_FULFILLED: () => handleFetchFulfilled(dispatch, action, state, nextState),
    };

    if (actions[action.type]) {
        return actions[action.type]();
    }
};

function handleAddIssue(dispatch, nextState) {
    const issue = get(nextState, 'formData.flagMetadataIssue.values', {});
    const issues = get(nextState, 'formData.flagMetadataIssues.values.issues', []);
    const nextIssueId = !isEmpty(issues) ? max(map(issues, 'issueId')) + 1 : 0;

    dispatch({
        fieldName: 'issues',
        formName: 'flagMetadataIssues',
        type: 'FORM_SET_FIELD_VALUE',
        value: [
            ...filter(
                issues,
                ({fieldName, issueType}) => fieldName !== issue.fieldName || issueType !== issue.issueType,
            ),
            {
                ...issue,
                comment: issue.comment ? issue.comment : null,
                issueId: nextIssueId,
            },
        ],
    });

    clearIssue(dispatch);
}

function handleReset(dispatch) {
    clearIssue(dispatch);

    dispatch({
        type: 'FORM_RESET',
        formName: 'flagMetadataIssues',
    });
}

function handleSetActive(dispatch, {active, roviProgramId, tmsId, sourceId}, nextState) {
    if (active) {
        updateIssuesFormData(dispatch, roviProgramId, tmsId, sourceId, nextState);
    }
}

function getAmsRecord(roviProgramId, tmsId, sourceId, nextState) {
    const servicesDataKey = get(nextState, 'flaggedMetadata.servicesDataKey');
    const searchPath =        sourceId != null
            ? servicesDataKey === 'bestMatches'
                ? 'result.sourceId'
                : 'sourceId'
            : tmsId !== null
              ? servicesDataKey === 'bestMatches'
                  ? 'result.tmsId'
                  : 'tmsId'
              : servicesDataKey === 'bestMatches'
                ? 'result.roviProgramId'
                : 'roviProgramId';

    return find(get(nextState, `servicesSearchData.${servicesDataKey}.searchData.content`), (content) => (sourceId != null
            ? get(content, searchPath) === sourceId
            : tmsId !== null
              ? get(content, searchPath) === tmsId
              : get(content, searchPath) === roviProgramId
    ));
}

function getFlaggedMetadataFromAmsRecord(roviProgramId, tmsId, sourceId, nextState) {
    const servicesDataKey = get(nextState, 'flaggedMetadata.servicesDataKey');
    const path = servicesDataKey === 'bestMatches' ? 'result.flaggedMetadata' : 'flaggedMetadata';

    return get(getAmsRecord(roviProgramId, tmsId, sourceId, nextState), path, {});
}

function updateIssuesFormData(dispatch, roviProgramId, tmsId, sourceId, nextState) {
    dispatch({
        type: 'FORM_SET_FIELD_VALUE',
        formName: 'flagMetadataIssues',
        fieldName: 'issues',
        value: get(getFlaggedMetadataFromAmsRecord(roviProgramId, tmsId, sourceId, nextState), 'issues'),
    });
}

function handleDeleteIssue(dispatch, {issueId}, nextState) {
    const issues = get(nextState.formData, 'flagMetadataIssues.values.issues', []);

    dispatch({
        type: 'FORM_SET_FIELD_VALUE',
        formName: 'flagMetadataIssues',
        fieldName: 'issues',
        value: filter(issues, (data) => data.issueId !== issueId),
    });

    clearIssue(dispatch);
}

function handleFetchFulfilled(
    dispatch,
    {path = '', query = {}, searchData: {content = []} = {}, servicesDataKey},
    state,
    nextState,
) {
    if (servicesDataKey === 'flaggedMetadata') {
        setStatusFormValues(dispatch, content);
    } else if (servicesDataKey === 'flaggedMetadataDetail') {
        setCommentsFormValues(dispatch, get(content, '0.issues', []));
    } else if (
        servicesDataKey === 'moviesAndProgramsMappingFranchise'
        || servicesDataKey === 'moviesAndProgramsMappingEpisodes'
        || servicesDataKey === 'bestMatches'
    ) {
        const {servicesSearchData: {[servicesDataKey]: {path: oldPath = '', query: oldQuery = {}}} = {}} = state;
        const {flaggedMetadata: {active, servicesDataKey: activeServicesDataKey} = {}} = nextState;

        if (active) {
            if (activeServicesDataKey === servicesDataKey && (!isEqual(oldQuery, query) || oldPath !== path)) {
                dispatch({
                    type: 'FLAGGED_METADATA_RESET',
                });
            } else {
                updateIssuesFormData(
                    dispatch,
                    get(nextState, 'flaggedMetadata.roviProgramId'),
                    get(nextState, 'flaggedMetadata.tmsId'),
                    nextState,
                );
            }
        }
    }
}

function handleFormSubmitRejected(dispatch, {formName}, nextState) {
    if (formName === 'flaggedMetadataStatus') {
        setStatusFormValues(dispatch, get(nextState, 'servicesSearchData.flaggedMetadata.searchData.content'));
    } else if (formName === 'flaggedMetadataComments') {
        setCommentsFormValues(
            dispatch,
            get(nextState, 'servicesSearchData.flaggedMetadataDetail.searchData.content.0.issues'),
        );
    }
}

function setStatusFormValues(dispatch, values) {
    dispatch({
        formName: 'flaggedMetadataStatus',
        type: 'FORM_SET_FIELD_VALUES',
        values: reduce(
            values,
            (obj, {flaggedMetadataId, status}) => {
                if (status === 'IN_PROGRESS') {
                    obj.inProgress.push(flaggedMetadataId.toString());
                } else if (status === 'FIXED') {
                    obj.fixed.push(flaggedMetadataId.toString());
                }
                return obj;
            },
            {
                inProgress: [],
                fixed: [],
            },
        ),
    });
}

function setCommentsFormValues(dispatch, values) {
    dispatch({
        formName: 'flaggedMetadataComments',
        type: 'FORM_SET_FIELD_VALUES',
        values: reduce(
            values,
            (obj, {issueId, comment}) => {
                obj[issueId] = comment;
                return obj;
            },
            {},
        ),
    });
}

function handleFormFieldUpdate(dispatch, {fieldName, formName, value}, state, nextState) {
    if (formName === 'flaggedMetadataStatus' && (fieldName === 'inProgress' || fieldName === 'fixed')) {
        const {inProgress, fixed} = get(state, ['formData', formName, 'values'], {});
        const {inProgress: nextInProgress, fixed: nextFixed} = get(nextState, ['formData', formName, 'values'], {});
        const addedInProgressIds = difference(nextInProgress, inProgress);
        const addedFixedIds = difference(nextFixed, fixed);
        const [lastId, status] = !isEmpty(addedInProgressIds)
            ? [addedInProgressIds[0], 'IN_PROGRESS']
            : !isEmpty(addedFixedIds)
              ? [addedFixedIds[0], 'FIXED']
              : [union(xor(inProgress, nextInProgress), xor(fixed, nextFixed))[0], 'OPEN'];
        const newValues = {
            inProgress: status === 'FIXED' ? difference(nextInProgress, addedFixedIds) : nextInProgress,
            fixed: status === 'IN_PROGRESS' ? difference(nextFixed, addedInProgressIds) : nextFixed,
            status,
        };

        dispatch({
            formName,
            type: 'FORM_SET_FIELD_VALUES',
            values: newValues,
        });

        dispatch({
            formName,
            customPath: lastId,
            type: 'SET_FORM_CUSTOM_PATH',
        });

        dispatch({
            formName,
            type: 'FORM_SUBMIT_FETCH',
        });

        dispatch({
            formName,
            type: 'FORM_SET_FIELD_VALUE',
            fieldName: 'status',
            value: undefined,
        });
    } else if (formName === 'flagMetadataIssue' && fieldName === 'fieldName') {
        const issue = find(
            get(nextState, 'formData.flagMetadataIssues.values.issues'),
            ({fieldName}) => fieldName === value,
        );

        if (issue) {
            dispatch({
                formName,
                type: 'FORM_SET_FIELD_VALUES',
                values: pick(issue, ['fieldName', 'issueType', 'comment']),
            });
        }
    }
}

function saveComments(dispatch, {issueId}, nextState) {
    const formName = 'flaggedMetadataComments';
    const formData = get(nextState, `formData.${formName}.values`, {});
    const amsIssue =        find(
            get(nextState, 'servicesSearchData.flaggedMetadataDetail.searchData.content.0.issues', []),
            (item) => item.issueId === issueId,
        ) || {};

    dispatch({
        type: 'FORM_SET_FIELD_VALUE',
        formName,
        fieldName: 'amsIssue',
        value: {
            ...amsIssue,
            comment: get(formData, issueId),
        },
    });

    dispatch({
        formName,
        customPath: issueId,
        type: 'SET_FORM_CUSTOM_PATH',
    });

    dispatch({
        formName,
        type: 'FORM_SUBMIT_FETCH',
    });

    dispatch({
        formName,
        type: 'FORM_SET_FIELD_VALUE',
        fieldName: 'amsIssue',
        value: undefined,
    });
}

function clearIssue(dispatch) {
    dispatch({
        type: 'FORM_RESET',
        formName: 'flagMetadataIssue',
    });
}

function postFlaggedMetadata(dispatch, {onSuccess}, nextState) {
    const {roviProgramId, sourceId, tmsId} = get(nextState, 'flaggedMetadata', {});
    const issues = get(nextState, 'formData.flagMetadataIssues.values.issues', []);
    const query =        sourceId != null
            ? {
                  sourceId,
                  issues,
              }
            : tmsId !== null
              ? {
                    tmsId,
                    issues,
                }
              : {
                    roviProgramId,
                    issues,
                };
    const dispatchFetched = (jsonResponse) => {
        dispatch({
            roviProgramId,
            sourceId,
            tmsId,
            formName: 'mapMediaItem',
            onSuccess,
            response: jsonResponse,
            type: 'FLAGGED_METADATA_POST_FULFILLED',
        });
    };
    const dispatchError = (error) => {
        dispatch({
            error,
            type: 'FLAGGED_METADATA_POST_REJECTED',
        });
    };
    const flaggedMetadataId = get(
        getFlaggedMetadataFromAmsRecord(roviProgramId, tmsId, sourceId, nextState),
        'flaggedMetadataId',
    );
    const actionParams = {
        servicesConfig: flaggedMetadataId ? servicesFormConfig.flaggedMetadataUpdate : servicesFormConfig.flagMetadata,
        dispatchFetched,
        dispatchError,
    };

    dispatch({
        type: 'FLAGGED_METADATA_POST',
    });

    return Promise.resolve(actionsServiceCaller(actionParams, query, flaggedMetadataId));
}

function handleCompleteFlagging(dispatch, {roviProgramId, sourceId, tmsId, onSuccess}, nextState) {
    const {onSuccessCallbacks = {}, servicesDataKey} = get(nextState, 'flaggedMetadata', {});

    if (onSuccess) {
        if (onSuccess === 'createShell') {
            const amsProgram = getAmsRecord(roviProgramId, tmsId, sourceId, nextState);
            const echostarContentType = get(amsProgram, 'echostarContentType') === 'SRE' ? 'episode' : 'franchise';

            dispatch({
                type: 'MAPPING_SET_FLAGGED_ROVI_TMSID_PROGRAM_ID',
                flaggedRoviProgramId: {
                    [echostarContentType]: roviProgramId,
                },
                flaggedTmsId: {
                    [echostarContentType]: tmsId,
                },
                flaggedSourceId: {
                    [echostarContentType]: sourceId,
                },
            });
        }

        if (isFunction(onSuccessCallbacks[onSuccess])) {
            onSuccessCallbacks[onSuccess]();
        }
    }

    dispatch({type: 'SEARCH_DATA_SEARCH', servicesDataKey});

    dispatch({
        type: 'FLAGGED_METADATA_RESET',
    });
}

export default flaggedMetadataMiddleware;
